import { inject, observer } from 'mobx-react';
import dynamic from 'next/dynamic';
import { useCallback, useMemo, useRef } from 'react';
import ComponentsErrorBoundary from '@/components/ErrorBoundary/ComponentsErrorBoundary';
import TabPersonalizedConfig from '../VersionPersonalization/data.config';
import { ProductTracker } from '@/utils/Tracker';
import CommodityModal from './components/CommodityModal';
import styles from './index.module.less';
import NoDescCommodityItem from '../VersionItem/NoDescCommodityItem';
import ItemGiftsWrap from '../ItemGiftsWrap';
import { isEmpty } from 'lodash';
import { useThrottleFn } from 'ahooks';

const VersionPersonalization = dynamic(import('../VersionPersonalization'));
const VersionCommodityItem = dynamic(import('../VersionItem/CommodityItem'));

const EXPECT_CIDS = [523, 549, 590, 636]; // 即使一个套餐也展示套餐选项

/**
 * 套餐list，仅有一个套餐时不展示，除了EXPECT_CIDS这里面的产品，同时套餐简介拼接至产品简介,
 *
 * 2025.02.10更新：主机产品页仅有一个套餐时也展示：
 * 【PROD】：https://arashivision.feishu.cn/wiki/XAgpwLWpQiYmZpktyxzcbB2cnZe
 */
function Index(props) {
  const {
    curCommodityList,
    setStateValue,
    productDetail: { id: pid, VersionConfig, is_camera },
    curSelectCommodity: { info: cInfo },
  } = props.product;
  const { LANG } = props.area;
  const { style } = props;

  const modalRef = useRef(null);

  const { run: onSelectCommodity } = useThrottleFn(
    (cid) => {
      setStateValue('curSelectCommodityId', cid);
      setStateValue('isSelectPersonalized', false);
    },
    { wait: 500, leading: true, trailing: false }
  );

  // 选中自定义套餐
  const onSelectPersonalCommodity = useCallback(
    (cid) => {
      setStateValue('curSelectCommodityId', cid);
      setStateValue('isSelectPersonalized', true);
    },
    [setStateValue]
  );

  /** curCommodityList是通过计算得到，每次获取都是一个新的内存地址，导致列表会刷新，取套餐列表ID作为更新条件 */
  const saleStateListCondition = curCommodityList.map((d) => d.id)?.join();

  // 返回有库存 & 无库存套餐列表
  const saleStateList = useMemo(() => {
    return [curCommodityList.filter((d) => !d.isOutOfStock), curCommodityList.filter((d) => d.isOutOfStock)];
  }, [saleStateListCondition]);

  const EnabledComponent = (key) => {
    switch (key) {
      case 'VersionPersonalizedEntry':
        if (!TabPersonalizedConfig.enabled({ pid })) return null;
        return <VersionPersonalization onChangeCommodity={onSelectPersonalCommodity} />;
      default:
        return null;
    }
  };

  /** 展示商品详情弹窗 */
  const onShowModal = (commodity) => {
    modalRef.current?.onShow(commodity);
    ProductTracker.productPage({
      action: 'click_bundle_popup',
      productState: props.product,
      label: commodity.id,
    });
  };

  // 套餐项组件，该组件只有saleStateList状态改变才会重新渲染
  const VersionCommodityItemComponent = (props) => {
    const { commodity } = props;
    /** 一些特殊产品页仍用以前的样式 */
    const isNoDescCommodityItem = VersionConfig.groupStyle?.commodityDescBottom;

    switch (true) {
      case isNoDescCommodityItem:
        return (
          <NoDescCommodityItem
            key={commodity.id}
            onChangeCommodity={onSelectCommodity}
            commodity={commodity}
            labels={commodity.tagInfosGroup}
            className={styles.commodityItem}
          />
        );
      default:
        return (
          <VersionCommodityItem
            key={commodity.id}
            onChangeCommodity={onSelectCommodity}
            commodity={commodity}
            onShowModal={onShowModal}
            labels={commodity.tagInfosGroup}
            className={styles.commodityItem}
          />
        );
    }
  };

  // 套餐列表
  const getCommodityList = useCallback(
    (index) => {
      return saleStateList[index]?.map((commodity) => {
        return <VersionCommodityItemComponent commodity={commodity} key={commodity.id} />;
      });
    },
    [saleStateList]
  );

  // 非主机页面，只有一个套餐不展示列表
  // 增值服务产品页通过 EXPECT_CIDS 来特殊处理，展示单SKU列表
  if (curCommodityList.length <= 1 && VersionConfig?.type !== 'group' && !EXPECT_CIDS.includes(pid) && !is_camera)
    return null;

  return (
    <div className={styles.list} style={style}>
      <h2>
        <div id='version_commodity_title' className={styles.title}>
          {VersionConfig.versionCommodityListTitle ?? LANG['productNew.version_commodity_title']}
        </div>
      </h2>
      <div className={styles.content} data-items={VersionConfig.groupStyle?.commodityitemCountInRow}>
        {/* 未缺货套餐 */}
        {getCommodityList(0)}

        <ComponentsErrorBoundary namg='TopInfo VersionPersonalizedEntry'>
          {EnabledComponent('VersionPersonalizedEntry')}
        </ComponentsErrorBoundary>

        {/* 缺货套餐 */}
        {getCommodityList(1)}
      </div>

      {/* 配置描述放在列表下方展示，不显示在套餐下方 */}
      {VersionConfig.groupStyle?.commodityDescBottom && (
        <div className={styles.itemIntro}>
          <div className={styles.introWrap} dangerouslySetInnerHTML={{ __html: cInfo.introduction }} />
          <ItemGiftsWrap style={{ marginTop: isEmpty(cInfo.introduction, true) ? 0 : 24 }} />
        </div>
      )}
      <CommodityModal ref={modalRef} onChangeCommodity={onSelectCommodity} />
    </div>
  );
}

const VersionCommodityList = inject('product', 'area', 'app')(observer(Index));

export default VersionCommodityList;
